import { DDL, StringValidation, Time } from 'types/general.types';
import { Link } from 'types/api.types';
import { ProfileCard } from 'types/profile';
import { MultiItemResponse } from 'types/generic.types';
import {
  LabelV2Version,
  ReleaseVersionContribution,
  SoundCloudVersion,
  TuneCoreVersion,
  Version,
} from './version.types';

export interface ReleaseSong {
  songGuid: string;
  overrides: {
    title?: string;
  };
  versions: Version[];
  snapshot: {
    title: string;
  };
}

export interface BaseRelease {
  guid: string;
  type: ReleaseType;
  title: string;
  updated: Time;
  created: Time;
  createBy: {
    userGuid: string;
    profileCard: ProfileCard;
  };
  originalAudioGuids?: string[];
  _links?: {
    createReleaseVersion?: Link;
    updateRelease?: Link;
    updateReleaseSongOverrides?: Link;
    exportRelease?: Link;
    getReleaseVersion?: Link;
    updateReleaseVersionMetadata?: Link;
    deleteReleaseVersion?: Link;
    deleteReleaseSong?: Link;
    moveReleaseVersion?: Link;
    exportReleaseExport?: Link;
    updateReleaseExport?: Link;
    listReleaseVersions?: Link;
  };
  latestExportShipment?: {
    parentGuid: string;
    exportGuid: string;
    exportShipmentGuid: string;
    labelId: string;
    status: ReleaseExportStatus;
    updated: Time;
  };
}

export interface LabelRelease extends BaseRelease {
  songs: ReleaseSong[];
}

export interface LabelV2Release extends BaseRelease {
  isReadyForExportReason?: ExportReason;
  versions: LabelV2Version[];
  isInvalid: boolean;
}

export type ExportReason = 'Processing' | 'Ready';

export interface SoundCloudRelease extends BaseRelease {
  isReadyForExportReason?: ExportReason;
  versions: SoundCloudVersion[];
}

export interface TuneCoreRelease extends BaseRelease {
  isReadyForExportReason?: ExportReason;
  versions: TuneCoreVersion[];
  latestExport?: {
    guid?: string;
    created?: Time;
    updated?: Time;
    createdBy?: { userGuid: string };
    status: ReleaseExportStatus;
    tuneCore?: {
      accountConnectionGuid: string;
      genre: string;
      language: string;
      mainArtist: string;
      recordingLocation: string;
      releaseDate: string;
      title: string;
      callbackResponse?: {
        songs: Array<{ status: string }>;
        tuneCoreReleaseUrl: string;
        releaseExportGuid: string;
      };
    };
  };
}

export enum ReleaseExportStatus {
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAIL',
  PROCESSING = 'PROCESSING',
}

export interface _ReleasesListLinks {
  next?: Link;
}

export interface ReleaseVersionTypesDDl {
  types: DDL;
}
export type Release =
  | LabelRelease
  | SoundCloudRelease
  | TuneCoreRelease
  | LabelV2Release;

export interface GroupedVersionCredits {
  contribution: ReleaseVersionContribution;
  contributions: ReleaseVersionContribution[];
}

export interface UpdateReleaseVersionValidations {
  versionTitle: StringValidation;
  description: StringValidation;
}

export interface CreateReleaseValidations {
  songs: {
    maxItems: number;
  };
  type: {
    enum: string[];
  };
}

export type PatchOperation = 'ADD' | 'REMOVE';

export interface GenrePayload {
  op: PatchOperation;
  genre: string;
}

export interface CreateReleaseSongRequestData {
  guid: string;
  type: string;
}

export interface AttachAudioVersionsToReleaseData {
  songGuid: string;
  audioGuid: string;
  type: string;
}

export interface ExportReleaseData {
  projectId?: string;
  label: string;
  customLabelName?: string;
}

export enum ReleaseType {
  LABEL = 'Label',
  LABEL_V2 = 'LabelV2',
  SOUNDCLOUD = 'SoundCloud',
  TUNECORE = 'TuneCore',
}

export interface ReleaseVersionReorderBody {
  versionGuid: string;
  afterVersionGuid?: string;
}

export interface PlaylistTypesListItem {
  id?: string;
  key: string;
  name: string;
}

export interface PlaylistTypesResponse {
  playlistTypes: {
    [key: string]: {
      key: string;
      name: string;
    };
  };
}

export type ListReleaseVersionsResponse = MultiItemResponse<
  {
    lyrics: {
      isExcluded: boolean;
    };
  },
  'releaseVersion'
>;
